import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import RegisterCTA from "../../buttons/RegisterCTA";

const useStyles = makeStyles((theme) => ({
  appBar: {
    transition: "background-color 0.25s ease, border-color 0.25s ease",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1100,
  },
  appBarTransparent: {
    backgroundColor: "rgba(15, 23, 42, 0.75)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
  },
  appBarSolid: {
    backgroundColor: "rgba(15, 23, 42, 0.96)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
    boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.25)",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: "1200px",
    width: "100%",
    margin: "0 auto",
    padding: "0 24px",
    minHeight: "64px",
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    textDecoration: "none",
  },
  logo: {
    height: "32px",
    width: "auto",
    objectFit: "contain",
  },
  navGroup: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  navLink: {
    color: "#cbd5e1",
    fontSize: "14px",
    fontWeight: 500,
    textTransform: "none",
    padding: "6px 14px",
    borderRadius: "8px",
    transition: "all 0.15s ease",
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
  },
  navLinkActive: {
    color: "#ffffff",
    fontWeight: 600,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  dropdownPaper: {
    backgroundColor: "#1e293b",
    border: "1px solid #334155",
    borderRadius: "10px",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.4)",
    padding: "4px",
    marginTop: "6px",
    minWidth: "180px",
  },
  dropdownItem: {
    color: "#cbd5e1",
    fontSize: "14px",
    fontWeight: 500,
    borderRadius: "6px",
    padding: "8px 14px",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "rgba(222, 20, 140, 0.15)",
      color: "#ffffff",
    },
  },
  mobileMenuToggle: {
    color: "#ffffff",
    padding: "6px",
    borderRadius: "8px",
    backgroundColor: "rgba(255, 255, 255, 0.06)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
  },
  mobileDrawer: {
    position: "fixed",
    top: "64px",
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#0f172a",
    zIndex: 1050,
    display: "flex",
    flexDirection: "column",
    padding: "20px",
    gap: "8px",
    overflowY: "auto",
  },
  mobileLink: {
    color: "#cbd5e1",
    fontSize: "15px",
    fontWeight: 500,
    padding: "12px 14px",
    borderRadius: "8px",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.06)",
      color: "#ffffff",
    },
  },
}));

function NavigationGeneral() {
  const classes = useStyles();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [openHelp, setOpenHelp] = useState(false);
  const anchorRefHelp = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenHelp(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <AppBar
        elevation={0}
        className={`${classes.appBar} ${
          isScrolled ? classes.appBarSolid : classes.appBarTransparent
        }`}
      >
        <Toolbar disableGutters className={classes.toolbar}>
          {/* Logo */}
          <Link to="/" className={classes.logoContainer}>
            <img
              src="https://ik.imagekit.io/urhub/assets/misc/logo-white-only.png"
              alt="UR Logo"
              className={classes.logo}
            />
            <span
              style={{
                color: "#ffffff",
                fontWeight: 700,
                fontSize: "18px",
                letterSpacing: "-0.02em",
              }}
            >
              
            </span>
          </Link>

          {/* Desktop Nav Items */}
          <nav className={classes.navGroup}>
            <Button
              component={Link}
              to="/"
              className={`${classes.navLink} ${
                isActive("/") ? classes.navLinkActive : ""
              }`}
            >
              Beranda
            </Button>

            <Button
              component={Link}
              to="/pricing"
              className={`${classes.navLink} ${
                isActive("/pricing") ? classes.navLinkActive : ""
              }`}
            >
              Harga
            </Button>

            <Button
              component={Link}
              to="/partner"
              className={`${classes.navLink} ${
                isActive("/partner") ? classes.navLinkActive : ""
              }`}
            >
              Partner
            </Button>

            <Button
              component={Link}
              to="/blog"
              className={`${classes.navLink} ${
                isActive("/blog") ? classes.navLinkActive : ""
              }`}
            >
              Blog
            </Button>

            {/* Bantuan Dropdown */}
            <Button
              ref={anchorRefHelp}
              onClick={() => setOpenHelp((prev) => !prev)}
              className={`${classes.navLink} ${
                location.pathname.startsWith("/pusat-bantuan")
                  ? classes.navLinkActive
                  : ""
              }`}
              endIcon={<KeyboardArrowDownIcon style={{ fontSize: 18 }} />}
            >
              Bantuan
            </Button>
            <Popper
              open={openHelp}
              anchorEl={anchorRefHelp.current}
              transition
              disablePortal
              placement="bottom-start"
              style={{ zIndex: 1200 }}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper className={classes.dropdownPaper}>
                    <ClickAwayListener
                      onClickAway={(e) => {
                        if (
                          anchorRefHelp.current &&
                          anchorRefHelp.current.contains(e.target)
                        )
                          return;
                        setOpenHelp(false);
                      }}
                    >
                      <MenuList autoFocusItem={openHelp}>
                        <MenuItem
                          component={Link}
                          to="/pusat-bantuan/user"
                          className={classes.dropdownItem}
                          onClick={() => setOpenHelp(false)}
                        >
                          Bantuan Pengguna
                        </MenuItem>
                        <MenuItem
                          component={Link}
                          to="/pusat-bantuan/partner"
                          className={classes.dropdownItem}
                          onClick={() => setOpenHelp(false)}
                        >
                          Bantuan Partner
                        </MenuItem>
                        <MenuItem
                          component={Link}
                          to="/faqs"
                          className={classes.dropdownItem}
                          onClick={() => setOpenHelp(false)}
                        >
                          FAQs
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>

            <Button
              component={Link}
              to="/karir"
              className={`${classes.navLink} ${
                isActive("/karir") ? classes.navLinkActive : ""
              }`}
            >
              Karir
            </Button>

            <Button
              component={Link}
              to="/contact"
              className={`${classes.navLink} ${
                isActive("/contact") ? classes.navLinkActive : ""
              }`}
            >
              Kontak
            </Button>
          </nav>

          {/* Right CTA */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div className="hidden sm:block">
              <RegisterCTA navBar={true} />
            </div>

            <div className="sm:hidden block">
              <IconButton
                aria-label="menu"
                className={classes.mobileMenuToggle}
                onClick={() => setMobileMenuOpen((prev) => !prev)}
              >
                {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            </div>
          </div>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className={classes.mobileDrawer}>
          <Link to="/" className={classes.mobileLink}>
            Beranda
          </Link>
          <Link to="/pricing" className={classes.mobileLink}>
            Paket &amp; Harga
          </Link>
          <Link to="/partner" className={classes.mobileLink}>
            Partner Merchant
          </Link>
          <Link to="/blog" className={classes.mobileLink}>
            Blog
          </Link>
          <Link to="/pusat-bantuan/user" className={classes.mobileLink}>
            Bantuan Pengguna
          </Link>
          <Link to="/pusat-bantuan/partner" className={classes.mobileLink}>
            Bantuan Partner
          </Link>
          <Link to="/faqs" className={classes.mobileLink}>
            FAQs
          </Link>
          <Link to="/karir" className={classes.mobileLink}>
            Karir
          </Link>
          <Link to="/contact" className={classes.mobileLink}>
            Kontak
          </Link>
          <div style={{ marginTop: "12px" }}>
            <RegisterCTA width="100%" />
          </div>
        </div>
      )}
    </>
  );
}

export default NavigationGeneral;
